import { memo, useMemo } from "react";

import { ProductProvider, parseGid, useProduct } from "@shopify/hydrogen-react";
import {
  type Maybe,
  type FilterValue,
} from "@shopify/hydrogen-react/storefront-api-types";

import { type BadgeType } from "@components/Badge";
import { type HeadingSize } from "@components/Heading";
import { ProductCardPlaceholder } from "@components/ProductCard/ProductCardPlaceholder.view";
import { type SizeValue } from "@components/Text";
import { handleize, isColorOption } from "@react/utilities";
import { type WithBreakpoints } from "@react/utilities";
import { SelectedVariantAvailability } from "@root/tablescape/constants";
import { type OPProductConnection } from "@root/tablescape/state";
import { formatProduct, formatProductVariant } from "@tablescape/utilities";

import { ProductCardView } from "./ProductCard.view";

type ProductCardPresentation = "default" | "carousel";

type ProductCardPresentationConfig = {
  priceSize: SizeValue | WithBreakpoints<SizeValue>;
  titleHeadingLevel: "h4" | "h6";
  titleHeadingSize?: HeadingSize | WithBreakpoints<HeadingSize>;
};

const productCardPresentationConfig: Record<
  ProductCardPresentation,
  ProductCardPresentationConfig
> = {
  default: {
    priceSize: "md",
    titleHeadingLevel: "h6",
  },
  carousel: {
    priceSize: { sm: "md", md: "lg" },
    titleHeadingLevel: "h4",
    titleHeadingSize: { sm: "xs", md: "sm" },
  },
};

type props = {
  appliedColorFilters?: FilterValue[];
  bundleValueText: string;
  enableQuickAdd?: boolean;
  nextLabel: string;
  pageType?: string;
  presentation?: ProductCardPresentation;
  prevLabel: string;
  quickAddButtonText?: string;
  variantMatchCollectionName?: string;
};

export const ProductCardBase = ({
  appliedColorFilters,
  bundleValueText,
  enableQuickAdd,
  nextLabel,
  pageType,
  presentation,
  prevLabel,
  quickAddButtonText,
  variantMatchCollectionName,
}: props) => {
  const { product, selectedVariant } = useProduct();
  const presentationConfig =
    productCardPresentationConfig[presentation ?? "default"];

  const formattedProduct = useMemo(() => formatProduct(product), [product]);

  const formattedSelectedVariant = useMemo(
    () => formatProductVariant(selectedVariant),
    [selectedVariant]
  );

  const colorOption = useMemo(
    () => formattedProduct.options?.find((option) => isColorOption(option)),
    [formattedProduct.options]
  );

  const variants = useMemo(() => {
    return formattedProduct.variants.filter((variant) => !variant.hidden);
  }, [formattedProduct.variants]);

  const activeVariantUrl = useMemo(() => {
    return `${formattedProduct.onlineStoreUrl}?variant=${
      parseGid(selectedVariant.id).id
    }`;
  }, [selectedVariant, formattedProduct.onlineStoreUrl]);

  const resolvedBadgeType = useMemo<Maybe<BadgeType>>(() => {
    switch (formattedSelectedVariant.badgeType) {
      case "sage":
        return "tertiary";
      case "brick":
        return "quaternary";
      case "bright-blue":
        return "senary";
      default:
        return "primary";
    }
  }, [formattedSelectedVariant]);

  const truncatedLabels = useMemo(() => {
    return formattedProduct.labels?.slice(0, 3);
  }, [formattedProduct.labels]);

  const showQuickAdd = useMemo(() => {
    const { availableForSale, disableWaitlist, shippingNote } =
      formattedSelectedVariant;
    const isSoldOut = shippingNote
      ? handleize(shippingNote) === SelectedVariantAvailability.SOLD_OUT
      : false;
    const isWaitlistEnabledAndUnavailable =
      !disableWaitlist && !availableForSale;
    return !isSoldOut && !isWaitlistEnabledAndUnavailable;
  }, [formattedSelectedVariant]);

  if (!variants.length) {
    return null;
  }

  return product ? (
    <ProductCardView
      activeVariant={formattedSelectedVariant}
      activeVariantUrl={activeVariantUrl}
      appliedColorFilters={appliedColorFilters}
      badgeText={formattedSelectedVariant.badgeText}
      badgeType={resolvedBadgeType}
      bundleValueText={bundleValueText}
      colorOption={colorOption}
      compareAtPriceDisplay={formattedSelectedVariant.compareAtPriceDisplay}
      description={formattedProduct.description}
      enableQuickAdd={enableQuickAdd}
      labels={truncatedLabels}
      nextLabel={nextLabel}
      pageType={pageType}
      prevLabel={prevLabel}
      priceSize={presentationConfig.priceSize}
      productId={formattedProduct.id}
      quickAddButtonText={quickAddButtonText}
      setSize={formattedProduct.setSize}
      showQuickAdd={showQuickAdd}
      title={formattedProduct.title}
      titleHeadingLevel={presentationConfig.titleHeadingLevel}
      titleHeadingSize={presentationConfig.titleHeadingSize}
      variantMatchCollectionName={variantMatchCollectionName}
    />
  ) : (
    <ProductCardPlaceholder />
  );
};

const ProductCardWithProvider = ({
  appliedColorFilters,
  bundleValueText,
  enableQuickAdd,
  nextLabel,
  pageType,
  presentation,
  prevLabel,
  product,
  quickAddButtonText,
  variantMatchCollectionName,
}: {
  appliedColorFilters?: FilterValue[];
  bundleValueText: string;
  enableQuickAdd?: boolean;
  nextLabel: string;
  pageType?: string;
  presentation?: ProductCardPresentation;
  prevLabel: string;
  product: OPProductConnection;
  quickAddButtonText?: string;
  variantMatchCollectionName?: string;
}) => (
  <ProductProvider data={product}>
    <ProductCardBase
      appliedColorFilters={appliedColorFilters}
      bundleValueText={bundleValueText}
      enableQuickAdd={enableQuickAdd}
      nextLabel={nextLabel}
      pageType={pageType}
      presentation={presentation}
      prevLabel={prevLabel}
      quickAddButtonText={quickAddButtonText}
      variantMatchCollectionName={variantMatchCollectionName}
    />
  </ProductProvider>
);

export const ProductCard = memo(ProductCardWithProvider);
